/**
 * ProductTour modal — multi-page walkthrough available on demand.
 * Each page holds an optional .gif and a description.
 * Navigation is available via Back/Next buttons, page-dot indicators,
 * and a close (✕) button in the header.
 */
import { useState } from 'react';
import LazyImage from '../ui/LazyImage';

export type TourPage = {
    /** Short heading shown above the gif area */
    title: string;
    /** Body copy describing the feature demonstrated in the gif */
    description: string;
    /** Optional URL to the .gif file — leave undefined to show placeholder box */
    gifUrl?: string;
};

type ProductTourProps = {
    /** Ordered list of tour pages to display */
    pages: TourPage[];
    /** Called when the user dismisses the tour via ✕ or the last-page close action */
    onClose: () => void;
};

/**
 * Renders the gif area: a real <img> when gifUrl is provided, or a labelled
 * placeholder box so layout is preserved before assets are added.
 */
function TourGifArea({
    page,
    onPrev,
    onNext,
    disablePrev,
    disableNext,
}: {
    page: TourPage;
    onPrev: () => void;
    onNext: () => void;
    disablePrev?: boolean;
    disableNext?: boolean;
}) {
    if (page.gifUrl) {
        return (
            <div className='tour-gif-wrapper'>
                <button
                    className='tour-arrow-btn tour-arrow-left'
                    onClick={onPrev}
                    aria-label='Previous'
                    disabled={disablePrev}
                >
                    ‹
                </button>

                <LazyImage
                    src={page.gifUrl}
                    alt={`${page.title} demo`}
                    className='tour-gif-img'
                    wrapperStyle={{ display: 'block', width: '80%', height: '280px' }}
                />

                <button
                    className='tour-arrow-btn tour-arrow-right'
                    onClick={onNext}
                    aria-label='Next'
                    disabled={disableNext}
                >
                    ›
                </button>
            </div>
        );
    }

    return (
        <div
            className='tour-gif-wrapper tour-gif-placeholder'
            data-testid='tour-gif-placeholder'
            aria-label='Animation placeholder'
        >
            <span className='tour-gif-placeholder-label'>GIF coming soon</span>
        </div>
    );
}

/**
 * Row of clickable dot indicators showing the total number of pages and
 * the active page index.
 */
function TourDots({
    total,
    current,
    onSelect,
}: {
    total: number;
    current: number;
    onSelect: (index: number) => void;
}) {
    return (
        <div className='tour-dots' role='tablist'>
            {Array.from({ length: total }, (_, i) => (
                <button
                    key={i}
                    aria-label={`Go to page ${i + 1}`}
                    className={`tour-dot ${i === current ? 'tour-dot--active' : ''}`}
                    onClick={() => onSelect(i)}
                />
            ))}
        </div>
    );
}

export default function ProductTour({ pages, onClose }: ProductTourProps) {
    const [currentIndex, setCurrentIndex] = useState(0);

    const isFirst = currentIndex === 0;
    const isLast = currentIndex === pages.length - 1;
    const page = pages[currentIndex];

    const goBack = () => setCurrentIndex((prev) => Math.max(0, prev - 1));
    const goNext = () => setCurrentIndex((prev) => Math.min(pages.length - 1, prev + 1));

    return (
        <div className='modal-overlay' role='dialog' aria-modal='true' aria-label='Product tour'>
            <div className='tour-panel'>
                {/* Header */}
                <div className='tour-header'>
                    <span className='tour-page-counter'>
                        {currentIndex + 1} / {pages.length}
                    </span>
                    <span className='tour-title'>{page.title}</span>
                    <button
                        className='modal-btn-close tour-close-btn'
                        onClick={onClose}
                        aria-label='Close tour'
                    >
                        ✕
                    </button>
                </div>

                {/* Gif / description area */}
                <div className='tour-body'>
                    <TourGifArea page={page} onPrev={goBack} onNext={goNext} disablePrev={isFirst} disableNext={isLast} />
                    <p className='tour-description'>{page.description}</p>
                </div>

                {/* Footer: dots only (navigation via side arrows) */}
                <div className='tour-footer'>
                    <TourDots total={pages.length} current={currentIndex} onSelect={setCurrentIndex} />
                </div>
            </div>
        </div>
    );
}
